import React, { useEffect, useState } from 'react';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper } from '@mui/material';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  AdminBackgroundCheckFailedNotificationService,
  AdminFusionAuthUserDataService,
  AdminNoteService,
  AdminScammerNotificationService,
  Company,
  CompanyEmployeeService,
  CompanyService,
  Job,
  JobService,
  ParentService,
  Profile,
  SitterService,
  User,
  UserPIIRemovalService,
  UserService,
} from 'us-web-services';
import useAuthentication from '../../store/useAuthentication';
import useUser from '../../store/useUser';
import PageStyles from '../../styles/PageStyles';
import DisplayService from '../../util/DisplayService';
import ValidationService from '../../util/ValidationService';
import ConfirmDialog from '../common/ConfirmDialog';
import SitterEditForm from './SitterEditForm';
import ProfileEditForm from './ProfileEditForm';
import UserEditForm from './UserEditForm';

function UserEdit({ history, match }: RouteComponentProps<{ id: string }>) {
  const { classes } = PageStyles();
  const authenticationState = useAuthentication()[0];
  const adminUser = authenticationState.authentication.user;
  const userActions = useUser()[1];

  const [blockingParent, setBlockingParent] = useState(false);
  const [blockingFailedBackgroundCheck, setBlockingFailedBackgroundCheck] = useState(false);
  const [changedEmail, setChangedEmail] = useState(false);
  const [confirmBlockFailedBackgroundCheck, setConfirmBlockFailedBackgroundCheck] = useState(false);
  const [confirmDeletePii, setConfirmDeletePii] = useState(false);
  const [dialogText, setDialogText] = React.useState('');
  const [notificationCount, setNotificationCount] = React.useState(0);
  const [openJobCount, setOpenJobCount] = React.useState(0);
  const [profile, setProfile] = useState<Profile>({});
  const [removingPii, setRemovingPii] = useState(false);
  const [showConfirmBlock, setShowConfirmBlock] = React.useState(false);
  const [showSsoEmailDialog, setShowSsoEmailDialog] = useState(false);
  const [user, setUser] = useState<User>({});
  const [userJobs, setUserJobs] = useState<Job[]>([]);
  const [userCompany, setUserCompany] = useState<Company | null>(null);

  const isManagerAdmin = () => adminUser.roles.filter(role => role.code === 'MANAGER_ADMIN').length > 0;
  const isProductAdmin = () => adminUser.roles.filter(role => role.code === 'PRODUCT_ADMIN').length > 0;

  const getUserCompany = async (userId: number) => {
    setUserCompany(null);

    if (!userId) {
      return;
    }

    const companyEmployees = (await CompanyEmployeeService.findByFilter({ params: { userId } })).data?.data;
    const companyId = companyEmployees?.find(employee => employee.disabled === null)?.companyId;

    if (!companyId) {
      return;
    }

    const company = (await CompanyService.get(companyId)).data?.data;

    if (company) {
      setUserCompany(company);
    }
  };

  const getUserOpenJobs = async (userId: number): Promise<number> => {
    setUserJobs([]);

    if (!userId) {
      return 0;
    }

    try {
      const config = {
        params: {
          parentId: userId,
          status: 'open',
        },
      };

      const jobs = (await JobService.getByFilter(config)).data.data ?? [];

      setUserJobs(jobs);

      return jobs.length;
    } catch {
      toast.error(`Error fetching open jobs for user ${userId}`);

      return 0;
    }
  };

  useEffect(() => {
    if (!user.id || Number.parseInt(match.params.id, 10) !== user.id) {
      const getUser = async () => {
        const id = +match.params.id;

        const userConfig = {
          params: {
            fields: 'superUser',
          },
        };

        const userResponse = (await UserService.get(id, userConfig)).data;
        const userData = userResponse.data;

        getUserCompany(userData.id);
        setUser(userData);

        if (userData.parent) {
          const parentResponse = (await ParentService.get(userData.id)).data;

          setProfile(parentResponse.data);
        } else if (userData.sitter) {
          const sitterConfig = {
            params: {
              userId: adminUser.id,
            },
          };
          const sitterResponse = (await SitterService.get(userData.id, sitterConfig)).data;

          setProfile(sitterResponse.data);
        }
      };

      getUser();
    }
  }, [user.id, adminUser.id, match.params.id]);

  const showNotifyError = (error, userId: number) => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      `Could not notify caregivers about blocked user ${userId}`,
    );

    toast.error(displayedError.message);
  };

  const getScamNotificationCount = async (userId: number) => {
    try {
      const response = (await AdminScammerNotificationService.count({
        id: userId,
      })).data;

      return response.data.count;
    } catch {
      return 0;
    }
  };

  const sendScamNotifications = async () => {
    try {
      await AdminScammerNotificationService.send({
        id: user.id,
      });
    } catch (error) {
      showNotifyError(error, user.id);
    }
  };

  const showError = error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an error updating this user.',
    );

    toast.error(displayedError.message);
  };

  const saveUser = async (confirmedSsoEmail?: boolean) => {
    if (!ValidationService.isEmail(user.email)) {
      toast.error('Email is required and must be valid.');

      return;
    }

    if (changedEmail && !confirmedSsoEmail && isProductAdmin()
      && (userCompany?.isSsoRestricted || userCompany?.partners?.length > 0)) {
      setShowSsoEmailDialog(true);

      return;
    }

    const userData: User = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      active: user.active,
      roles: user.roles,
      policyAccepted: user.policyAccepted,
    };

    if (user.removePhoto) {
      userData.pictureUrl = '';
    }

    if (user.password) {
      if (user.password === user.password2) {
        userData.password = user.password;
      } else {
        toast.error('Passwords must match');

        return;
      }
    }

    try {
      const userResponse = (await UserService.patch(user.id, userData)).data.data;

      userResponse.sitter = user.sitter;
      userResponse.parent = user.parent;
      userResponse.superUser = user.superUser;

      userActions.setUser(userResponse);
    } catch (error) {
      showError(error);

      return;
    }

    const profileData: Profile = {
      id: user.id,
      address: profile.address,
      phone: profile.phone,
    };

    if (user.parent) {
      try {
        const parentResponse = (await ParentService.patch(user.id, profileData)).data.data;

        userActions.setProfile(parentResponse);
      } catch (error) {
        showError(error);

        return;
      }
    } else if (user.sitter) {
      profileData.dob = profile.dob;
      try {
        const sitterResponse = (await SitterService.patch(user.id, profileData)).data.data;

        userActions.setProfile(sitterResponse);
      } catch (error) {
        showError(error);

        return;
      }
    }

    const userRefreshData = {
      userId: user.id,
    };

    await AdminFusionAuthUserDataService.refresh(userRefreshData);

    toast.success('User updated successfully');
    history.push(`/users/lookup?id=${user.id}`);
  };

  const cancelEdit = async () => {
    history.push(`/users/lookup?id=${user.id}`);
  };

  const closeConfirmBlock = () => {
    setShowConfirmBlock(false);
  };

  const showConfirmDeletePii = () => {
    setConfirmDeletePii(true);
  };

  const showConfirmBlockFailedBackgroundCheck = () => {
    setConfirmBlockFailedBackgroundCheck(true);
  };

  const blockFailedBackgroundCheck = async () => {
    setConfirmBlockFailedBackgroundCheck(false);
    setBlockingFailedBackgroundCheck(true);

    try {
      if (!user.id) {
        throw new Error('User ID is required');
      }

      await AdminBackgroundCheckFailedNotificationService.send(user.id);
      await UserService.patch(user.id, { id: user.id, active: false });

      const noteData = {
        user: {
          id: user.id,
        },
        createdBy: {
          id: adminUser.id,
        },
        comment: 'Blocked for TOS - Failed Checkr',
      };

      await AdminNoteService.create(noteData);

      toast.success('Blocked and notified successfully');
    } catch (error) {
      showError(error);
    } finally {
      setBlockingFailedBackgroundCheck(false);
    }
  };

  const blockParent = async () => {
    setBlockingParent(true);

    try {
      await Promise.all(userJobs.map(async job => {
        const closedJob: Job = {
          id: job.id,
          status: 'closed_invalid',
        };

        try {
          await JobService.patch(closedJob.id, closedJob);
        } catch {
          toast.error(`Error closing job ${closedJob.id}`);
        }
      }));

      await UserService.patch(user.id, { id: user.id, active: false });

      let comment = 'Blocked for TOS - Scammer';

      if (openJobCount > 0) {
        comment += ` - ${openJobCount} job${openJobCount !== 1 ? 's' : ''} closed`;
      }

      if (notificationCount > 0) {
        await sendScamNotifications();
        comment += ` - ${notificationCount} caregiver${notificationCount !== 1 ? 's' : ''} notified`;
      }

      const noteData = {
        user: {
          id: user.id,
        },
        createdBy: {
          id: adminUser.id,
        },
        comment,
      };

      await AdminNoteService.create(noteData);

      let successMessage = 'User blocked';

      if (openJobCount > 0) {
        successMessage += `, ${openJobCount} job${openJobCount !== 1 ? 's' : ''} closed`;
      }

      if (notificationCount > 0) {
        successMessage += `, and ${notificationCount} caregiver${notificationCount !== 1 ? 's' : ''} notified.`;
      }

      toast.success(successMessage);
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        `Unexpected error blocking parent ${user.id}`,
      );

      toast.error(displayedError.message);
    } finally {
      setBlockingParent(false);
      setShowConfirmBlock(false);
      setNotificationCount(0);
      setOpenJobCount(0);
    }
  };

  const deletePii = async () => {
    setConfirmDeletePii(false);
    setRemovingPii(true);
    UserPIIRemovalService.delete(user.id)
      .then(() => {
        toast.success('User PII removed successfully');
      })
      .catch(error => {
        showError(error);
      })
      .finally(() => {
        setRemovingPii(false);
      });
  };

  const openBlockModal = async () => {
    const newJobCount = await getUserOpenJobs(user.id);
    const newNotificationCount = await getScamNotificationCount(user.id);

    let newText = 'Block this user';

    if (newJobCount) {
      if (newNotificationCount) {
        newText += `, close ${newJobCount} open job${newJobCount !== 1 ? 's' : ''}, `
          + `and notify ${newNotificationCount} caregiver`
          + `${newNotificationCount !== 1 ? 's' : ''} that this user has been removed?`;
      } else {
        newText += ` and close ${newJobCount} open job${newJobCount !== 1 ? 's' : ''}?`;
      }
    } else if (newNotificationCount) {
      newText += ` and notify ${newNotificationCount} caregiver`
        + `${newNotificationCount !== 1 ? 's' : ''} that this user has been removed?`;
    } else {
      newText += '? No open jobs to close or caregivers to notify.';
    }

    setOpenJobCount(newJobCount);
    setNotificationCount(newNotificationCount);
    setDialogText(newText);
    setShowConfirmBlock(true);
  };

  return (
    <Paper className={classes.paper}>
      <>
        <ConfirmDialog
          confirmAction={deletePii}
          open={confirmDeletePii}
          setOpen={setConfirmDeletePii}
          title='Confirm PII Removal'
          text="Are you sure you want to remove this user's PII?"
        />
        <ConfirmDialog
          confirmAction={blockFailedBackgroundCheck}
          open={confirmBlockFailedBackgroundCheck}
          setOpen={setConfirmBlockFailedBackgroundCheck}
          title='Confirm block and send failed background check notification'
          text="Are you sure you want to block this caregiver and notify they've been removed?"
        />
        <ConfirmDialog
          confirmAction={() => saveUser(true)}
          open={showSsoEmailDialog}
          setOpen={setShowSsoEmailDialog}
          title='Confirm SSO User Email Change'
          text={
            'WARNING: This user is SSO eligible. ' +
            'Updating their email may create a ' +
            'mismatch with FusionAuth that breaks SSO. Proceed?'
          }
        />
        <Dialog open={showConfirmBlock} onClose={closeConfirmBlock}>
          <DialogTitle>Confirm Block Action</DialogTitle>
          <DialogContent>{dialogText}</DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmBlock} color='secondary'>
              Cancel
            </Button>
            <Button autoFocus disabled={blockingParent} onClick={blockParent}>
              {`Block${notificationCount ? ` and Notify Caregiver${notificationCount !== 1 ? 's' : ''}` : ''}`}
            </Button>
          </DialogActions>
        </Dialog>
        {user.id ? (
          <UserEditForm
            canManageRoles={isManagerAdmin()}
            user={user}
            setChangedEmail={setChangedEmail}
            setUser={setUser}
          />
        ) : (
          'User not found'
        )}{' '}
        <br />
        {user.sitter && profile.id ? (
          <SitterEditForm profile={profile} setProfile={setProfile} />
        ) : (
          ' '
        )}
        {profile.id && (
          <ProfileEditForm profile={profile} setProfile={setProfile} />
        )}
        <div className={classes.buttons}>
          {user.sitter && profile.id ? (
            <Button
              disabled={blockingFailedBackgroundCheck}
              onClick={showConfirmBlockFailedBackgroundCheck}
              className={classes.button}
              style={{
                backgroundColor: 'red',
                color: 'white',
              }}
            >
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faBan}
              />
              Block for BG Check
            </Button>
          ) : null}
          {user.parent && profile.id ? (
            <Button
              disabled={blockingParent}
              onClick={openBlockModal}
              className={classes.button}
              style={{
                backgroundColor: 'red',
                color: 'white',
              }}
            >
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faBan}
              />
              Block Scammer
            </Button>
          ) : null}
          <Button
            disabled={removingPii}
            onClick={showConfirmDeletePii}
            className={classes.button}
            style={{
              backgroundColor: 'red',
              color: 'white',
            }}
          >
            <FontAwesomeIcon
              style={{
                fontSize: 16,
                marginRight: '0.75rem',
              }}
              icon={faTrash}
            />
            Delete PII
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={cancelEdit}
            className={classes.button}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => saveUser(false)}
            className={classes.button}
          >
            Save
          </Button>
        </div>
      </>
    </Paper>
  );
}

export default withRouter(UserEdit);
